"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Clear from "@mui/icons-material/Clear";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { useMiniCart } from "contexts/MiniCartContext";

import useCart from "hooks/useCart";
import MiniCartItem from "./components/cart-item";
import EmptyCartView from "./components/empty-view";
import { FlexBetween, FlexBox } from "@/components/flex-box";
import OverlayScrollbar from "@/components/overlay-scrollbar";
import { currency } from "lib";
import Image from "next/image";

export default function MiniCart() {
  const router = useRouter();
  const { state, addToCart, removeFromCart } = useCart();
  const CART_LENGTH = state.cart?.length || 0;
  const [loadingItemId, setLoadingItemId] = useState(null);
  const { open, setOpen } = useMiniCart();

  const handleCartAmountChange = (amount, product) => async () => {
    try {
      setLoadingItemId(product.id);
      await addToCart(product, amount);
    } finally {
      setLoadingItemId(null);
    }
  };

  const handleDeleteCartItem = (product) => async () => {
    try {
      setLoadingItemId(product.id);
      await removeFromCart(product);
    } finally {
      setLoadingItemId(null);
    }
  };

  const getTotalPrice = () =>
    state.cart?.reduce((acc, item) => acc + Number(item.price || 0) * Number(item.qty || 0), 0) || 0;

  const groupCartByVendor = (cart) =>
    (cart || []).reduce((groups, item) => {
      const vendorId = item.vendor?.id || "unknown";

      if (!groups[vendorId]) {
        groups[vendorId] = {
          vendor: item.vendor || {},
          items: [],
          total_order: 0,
          min_order: item.vendor?.min_order || 0,
        };
      }

      const price = Number(item.price) || 0;
      const qty = Number(item.qty) || 0;
      const subtotal = price * qty;

      groups[vendorId].total_order += subtotal;
      groups[vendorId].items.push(item);

      return groups;
    }, {});

  const groupedCart = groupCartByVendor(state.cart);
  const someVendorBelowMin = Object.values(groupedCart).some(
    (group) => group.total_order < group.min_order
  );

  return (
    <Box height="100vh" width={380} display="flex" flexDirection="column">
      {/* HEADER */}
      <FlexBetween ml={3} mr={2} height={74} flexShrink={0}>
        <FlexBox gap={1} alignItems="center">
          <ShoppingBagIcon />
        </FlexBox>
        <IconButton onClick={() => setOpen(false)}>
          <Clear />
        </IconButton>
      </FlexBetween>

      {/* BODY */}
      <Box flex="1 1 auto" overflow="hidden" mt={2}>
        {CART_LENGTH > 0 ? (
          <OverlayScrollbar>
            {Object.values(groupedCart).map((group) => (
              <Box key={group.vendor.id || Math.random()} mb={2}>
                <Box display="flex" alignItems="center" ml={2} mb={1}>
                  {group.vendor.logo ? (
                    <Image
                      src={group.vendor.logo}
                      alt={group.vendor.name || "Vendor"}
                      width={40}
                      height={40}
                      style={{ marginRight: 8, borderRadius: "50%", objectFit: "cover" }}
                      unoptimized
                    />
                  ) : (
                    <Box
                      width={40}
                      height={40}
                      mr={1}
                      borderRadius="50%"
                      bgcolor="#ccc"
                    />
                  )}
                  <Box justifyContent="space-between">
                    <strong>{group.vendor.name || "Desconocido"}</strong> <br />
                    {group.total_order < group.min_order && (
                      <small style={{ color: "red" }}>
                        Mínimo ${group.min_order} — te faltan{" "}
                        {currency(group.min_order - group.total_order)}
                      </small>
                    )}
                  </Box>
                </Box>
                {group.items.map((item) => (
                  <MiniCartItem
                    key={item.id}
                    item={item}
                    handleCartAmountChange={handleCartAmountChange}
                    onDelete={handleDeleteCartItem}
                    loading={loadingItemId === item.id}
                  />
                ))}
              </Box>
            ))}
          </OverlayScrollbar>
        ) : (
          <EmptyCartView />
        )}
      </Box>

      {/* FOOTER */}
      {CART_LENGTH > 0 && (
        <Box p={2.5} flexShrink={0} bgcolor="background.paper">
          <Button
            fullWidth
            variant="outlined"
            sx={{ height: 40 }}
            prefetch={false}
            LinkComponent={Link}
            color="primary"
            href="/cart"
            disabled={someVendorBelowMin}
          >
            Ver carrito ({currency(getTotalPrice())})
          </Button>

          {someVendorBelowMin && (
            <Typography variant="body2" color="error" mt={1} align="center">
              ⚠️ Algún vendedor no cumple con el pedido mínimo
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
